import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  X, Palette, Film, Shield, Layers, Mail, DollarSign, CheckSquare,
  Briefcase, Send, Search, Clapperboard, Bot, User, Building2, Star,
  Lightbulb, FileText, Users,
} from 'lucide-react'
import { useWorkspaceTabs } from '../../contexts/WorkspaceTabsContext'
import { zoomNavigate } from '../../lib/zoomNav'
import useShellPreview from '../shell/shellPreview'

// The open surfaces, as literal tabs under the block nav — newest first.
// Modules (Inbox, Finance…) tab themselves on visit; entity work surfaces
// (stage workspaces, expressions, properties) register with their titles.
// Closing the active tab moves to its neighbor.

const KIND_ICON = {
  stage: Palette, expression: Film, property: Shield, design: Palette,
  deal: Briefcase, contact: User, company: Building2,
  opportunity: Lightbulb, submission: Send, invoice: DollarSign,
  client: Star, task: CheckSquare, draft: FileText, interest: Users,
  janet: Bot,
}
const MODULE_ICON = {
  'module:janet': Bot,
  'module:inbox': Mail, 'module:finance': DollarSign, 'module:tasks': CheckSquare,
  'module:deals': Briefcase, 'module:submissions': Send, 'module:research': Search,
  'module:hub': Clapperboard, 'module:design': Palette, 'module:janetq': Bot,
  'module:contacts': User, 'module:companies': Building2, 'module:clients': Star,
  'module:properties': Shield, 'module:projects': Film,
}

export default function WorkspaceTabStrip() {
  const { tabs, closeTab, reorderTab } = useWorkspaceTabs()
  const shellPreview = useShellPreview()
  const location = useLocation()
  const navigate = useNavigate()
  const [dragId, setDragId] = useState(null)

  // Shared shell (preview): a lone tab is just the page you're on — skip it.
  if (!tabs.length || (shellPreview && tabs.length < 2)) return null

  const isActive = (tab) => tab.matches
    ? tab.matches.includes(location.pathname)
    : location.pathname === (tab.path || '').split('?')[0]

  const close = (e, tab) => {
    e.stopPropagation()
    const wasActive = isActive(tab)
    const idx = tabs.findIndex(t => t.id === tab.id)
    closeTab(tab.id)
    if (wasActive) {
      const neighbor = tabs[idx + 1] || tabs[idx - 1]
      if (neighbor && neighbor.id !== tab.id) zoomNavigate(navigate, neighbor.path)
    }
  }

  return (
    <div data-shell="workspace-tabs" className="flex items-end gap-1 px-2 pt-1 border-b border-[color:var(--ns-hair-soft)] bg-[color:var(--ns-paper)] overflow-x-auto shrink-0">
      {tabs.map(tab => {
        const active = isActive(tab)
        const Icon = MODULE_ICON[tab.id] || KIND_ICON[tab.kind] || Layers
        return (
          <button
            key={tab.id}
            onClick={() => !active && zoomNavigate(navigate, tab.path)}
            title={tab.title}
            data-active={active || undefined}
            draggable
            onDragStart={(e) => { setDragId(tab.id); e.dataTransfer.effectAllowed = 'move' }}
            onDragEnd={() => setDragId(null)}
            onDragOver={(e) => {
              // Slide the dragged tab left/right of whichever tab the cursor
              // is over, split at its midpoint — live, like browser tabs.
              e.preventDefault()
              if (!dragId || dragId === tab.id) return
              const rect = e.currentTarget.getBoundingClientRect()
              reorderTab(dragId, tab.id, (e.clientX - rect.left) < rect.width / 2)
            }}
            onDrop={(e) => e.preventDefault()}
            className={`group flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-t-md border border-b-0 text-[11.5px] max-w-[200px] shrink-0 transition-colors ${
              active
                ? 'bg-[color:var(--ns-raise)] border-[color:var(--ns-hair)] text-[color:var(--ns-ink)] font-medium -mb-px'
                : 'bg-[color:var(--ns-paper)] border-[color:var(--ns-hair-soft)] text-[color:var(--ns-gray)] hover:bg-[color:var(--ns-hair-soft)] hover:text-[color:var(--ns-ink)]'
            } ${dragId === tab.id ? 'opacity-40' : ''}`}
          >
            <Icon size={11} className={active ? 'text-[color:var(--ns-ink)]' : 'text-[color:var(--ns-faint)]'} />
            <span className="truncate">{tab.title}</span>
            <span
              onClick={(e) => close(e, tab)}
              className="p-0.5 rounded text-[color:var(--ns-faint)] hover:text-[color:var(--ns-ink-2)] hover:bg-[color:var(--ns-hair)] opacity-0 group-hover:opacity-100 transition-opacity"
              title="Close tab"
            >
              <X size={11} />
            </span>
          </button>
        )
      })}
    </div>
  )
}
